<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table';
import { Button } from '../button';
import { Input } from '../input';
import { Switch } from '../switch';
import { crossAppActions } from '../cross-app-actions';
import type { AppID, FacilityID } from '../../cross-app-shared/types/db';
import {
  type AppAccessOverview,
  type AppAccessTarget,
  appAccessTargetKey,
} from '../../cross-app-shared/services/app-access';
import {
  type AppAccessNode,
  appAccessEffective,
  appAccessFacilityCount,
  appAccessLevelLabels,
  appAccessRowsVisible,
  appAccessSettingOfCurrent,
  appAccessTreeBuild,
} from './app-access-tree';
import AppAccessEditDialog from './AppAccessEditDialog.vue';

const props = defineProps<{ facilityID: FacilityID | null }>();

const state = ref<
  { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'loaded'; overview: AppAccessOverview }
>({ kind: 'loading' });

async function overviewRefresh() {
  try {
    state.value = { kind: 'loaded', overview: await crossAppActions.appAccess.overviewGet() };
  } catch (e: any) {
    console.error(e);
    state.value = { kind: 'error', message: e.message };
  }
}

const apps = computed(() => (state.value.kind === 'loaded' ? state.value.overview.apps : []));
const rootsAll = computed(() => (state.value.kind === 'loaded' ? appAccessTreeBuild(state.value.overview) : []));

// Demo tenants are rarely relevant, so they're hidden unless asked for:
const demoTenantsShow = ref(false);
const demoTenantKeys = computed(
  () =>
    new Set(
      state.value.kind === 'loaded'
        ? state.value.overview.tenants
            .filter((t) => t.isDemo)
            .map((t) => appAccessTargetKey({ level: 'tenant', tenantID: t.tenantID }))
        : [],
    ),
);
const roots = computed(() =>
  demoTenantsShow.value ? rootsAll.value : rootsAll.value.filter((t) => !demoTenantKeys.value.has(t.key)),
);

const nodesByKey = computed(() => {
  const result = new Map<string, AppAccessNode>();
  const walk = (node: AppAccessNode) => {
    result.set(node.key, node);
    node.children.forEach(walk);
  };
  roots.value.forEach(walk);
  return result;
});

const search = ref('');
const expanded = ref(new Set<string>());

function expandedToggle(node: AppAccessNode) {
  const next = new Set(expanded.value);
  if (next.has(node.key)) {
    next.delete(node.key);
  } else {
    next.add(node.key);
  }
  expanded.value = next;
}

function expandToLevel(level: 'tenant' | 'group' | 'facility') {
  const next = new Set<string>();
  if (level !== 'tenant') {
    for (const node of nodesByKey.value.values()) {
      if (node.target.level === 'tenant' || (level === 'facility' && node.target.level === 'group')) {
        next.add(node.key);
      }
    }
  }
  expanded.value = next;
}

// Start with the facility the admin is currently looking at revealed, as they're often debugging that one facility:
const currentFacilityKey = computed(() =>
  props.facilityID ? appAccessTargetKey({ level: 'facility', facilityID: props.facilityID }) : null,
);

onMounted(async () => {
  await overviewRefresh();
  const tenantOfCurrent = rootsAll.value.find((tenant) =>
    tenant.children.some((group) => group.children.some((f) => f.key === currentFacilityKey.value)),
  );
  if (tenantOfCurrent && demoTenantKeys.value.has(tenantOfCurrent.key)) {
    demoTenantsShow.value = true;
  }

  const current = currentFacilityKey.value ? nodesByKey.value.get(currentFacilityKey.value) : null;
  if (current) {
    const next = new Set<string>();
    for (let ancestor = current.parent; ancestor; ancestor = ancestor.parent) {
      next.add(ancestor.key);
    }
    expanded.value = next;
  }
});

const rows = computed(() =>
  appAccessRowsVisible(roots.value, expanded.value, search.value).map((row) => ({
    ...row,
    cells: apps.value.map((app) => {
      const settingOf = appAccessSettingOfCurrent(app.appID);
      const effective = appAccessEffective(row.node, settingOf);
      const counts = row.node.target.level === 'facility' ? null : appAccessFacilityCount(row.node, settingOf);
      return {
        app,
        setting: row.node.settings[app.appID],
        effective,
        isExplicit: effective.decidedBy === row.node.target.level,
        // Only worth calling out when the facilities beneath don't all agree with this row's own effective access:
        counts:
          counts && counts.total > 0 && counts.granted !== (effective.isGrantedAccess ? counts.total : 0)
            ? counts
            : null,
      };
    }),
  })),
);

type Cell = (typeof rows.value)[number]['cells'][number];

function cellTitle(node: AppAccessNode, cell: Cell): string {
  const lines: string[] = [];
  if (cell.setting) {
    lines.push(
      `${cell.setting.isGrantedAccess ? 'Granted' : 'Denied'} on this ${appAccessLevelLabels[node.target.level].toLowerCase()} by ${cell.setting.createdByName} on ${new Date(cell.setting.createdAt).toLocaleDateString()}`,
    );
  } else if (cell.effective.decidedBy) {
    let source = node.parent;
    while (source && source.target.level !== cell.effective.decidedBy) {
      source = source.parent;
    }
    lines.push(`${cell.effective.isGrantedAccess ? 'Granted' : 'Denied'} via ${source?.name ?? 'a parent'}`);
  } else {
    lines.push('No access: nothing is set here or above');
  }

  if (cell.counts) {
    lines.push(`${cell.counts.granted} of ${cell.counts.total} facilities beneath have access`);
  }

  lines.push('Click to change');
  return lines.join('\n');
}

const levelIcons: Record<AppAccessTarget['level'], string> = {
  tenant: 'fa-light fa-building',
  group: 'fa-light fa-layer-group',
  facility: 'fa-light fa-industry-windows',
};

const editing = ref<{ node: AppAccessNode; app: { appID: AppID; name: string } } | null>(null);
const editOpen = ref(false);
function editStart(node: AppAccessNode, app: { appID: AppID; name: string }) {
  editing.value = { node, app };
  editOpen.value = true;
}
</script>

<template>
  <Card>
    <CardHeader>
      <CardTitle class="text-2xl font-bold">App Access</CardTitle>
      <CardDescription class="mt-1">
        Choose which apps each tenant, group, and facility can use. The most specific setting wins: grant at the tenant
        to cover everything beneath it, then override individual groups or facilities as needed.
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div class="mb-4 flex flex-wrap items-center gap-2">
        <div class="relative min-w-64 flex-1">
          <i class="fa-light fa-magnifying-glass text-muted-foreground absolute top-3 left-2.5 h-4 w-4"></i>
          <Input
            v-model="search"
            type="search"
            placeholder="Search tenants, groups, facilities, or IDs..."
            class="w-full pl-8"
          />
        </div>
        <Button variant="outline" size="sm" @click="expandToLevel('tenant')">
          <i class="fa-light fa-compress"></i>
          Collapse all
        </Button>
        <Button variant="outline" size="sm" @click="expandToLevel('group')">Show groups</Button>
        <Button variant="outline" size="sm" @click="expandToLevel('facility')">
          <i class="fa-light fa-expand"></i>
          Expand all
        </Button>
        <label class="ml-2 flex cursor-pointer items-center gap-2 text-sm whitespace-nowrap">
          <Switch v-model="demoTenantsShow" />
          Show {{ demoTenantKeys.size }} demo {{ demoTenantKeys.size === 1 ? 'tenant' : 'tenants' }}
        </label>
      </div>

      <div class="text-muted-foreground mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
        <span class="flex items-center gap-1.5">
          <span class="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-600 text-white">
            <i class="fa-solid fa-check text-[10px]"></i>
          </span>
          Granted here
        </span>
        <span class="flex items-center gap-1.5">
          <span
            class="inline-flex h-5 w-5 items-center justify-center rounded border border-dashed border-emerald-500 text-emerald-600"
          >
            <i class="fa-solid fa-check text-[10px]"></i>
          </span>
          Granted by a parent
        </span>
        <span class="flex items-center gap-1.5">
          <span class="inline-flex h-5 w-5 items-center justify-center rounded bg-red-600 text-white">
            <i class="fa-solid fa-ban text-[10px]"></i>
          </span>
          Denied here
        </span>
        <span class="flex items-center gap-1.5">
          <span
            class="inline-flex h-5 w-5 items-center justify-center rounded border border-dashed border-red-400 text-red-500"
          >
            <i class="fa-solid fa-ban text-[10px]"></i>
          </span>
          Denied by a parent
        </span>
        <span class="flex items-center gap-1.5">
          <span class="inline-flex h-5 w-5 items-center justify-center rounded text-slate-300">
            <i class="fa-light fa-minus text-[10px]"></i>
          </span>
          Not set anywhere (no access)
        </span>
        <span class="flex items-center gap-1.5">
          <span class="rounded bg-amber-100 px-1 font-medium text-amber-800">2/5</span>
          Facilities beneath with access, when they differ
        </span>
      </div>

      <div v-if="state.kind === 'loading'" class="text-muted-foreground py-8 text-center">
        <i class="fa-light fa-spinner fa-spin"></i>
        Loading app access...
      </div>
      <div v-else-if="state.kind === 'error'" class="py-8 text-center text-red-600">
        Unable to load app access: {{ state.message }}
      </div>
      <Table v-else>
        <TableHeader>
          <TableRow>
            <TableHead class="min-w-72">Name</TableHead>
            <TableHead v-for="app of apps" :key="app.appID" class="text-center whitespace-nowrap">
              {{ app.name }}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-if="rows.length === 0">
            <TableCell :colspan="apps.length + 1" class="text-muted-foreground py-8 text-center">
              Nothing matches your search
              <template v-if="!demoTenantsShow && demoTenantKeys.size > 0"> (demo tenants are hidden) </template>
            </TableCell>
          </TableRow>
          <TableRow
            v-for="row of rows"
            :key="row.node.key"
            :class="[
              row.node.key === currentFacilityKey ? 'bg-amber-50' : '',
              row.node.target.level === 'tenant' ? 'font-semibold' : '',
            ]"
          >
            <TableCell class="py-1.5">
              <div class="flex items-center gap-1.5" :style="{ paddingLeft: `${row.depth * 1.5}rem` }">
                <button
                  v-if="row.node.children.length > 0"
                  type="button"
                  class="text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-slate-100"
                  :aria-label="row.isExpanded ? 'Collapse' : 'Expand'"
                  @click="expandedToggle(row.node)"
                >
                  <i :class="row.isExpanded ? 'fa-light fa-chevron-down' : 'fa-light fa-chevron-right'"></i>
                </button>
                <span v-else class="h-6 w-6 shrink-0"></span>
                <i :class="levelIcons[row.node.target.level]" class="text-muted-foreground w-4"></i>
                <span :class="row.isSearchMatch ? 'rounded bg-yellow-100 px-1' : ''">{{ row.node.name }}</span>
                <span class="text-muted-foreground text-xs font-normal">
                  {{ row.node.idLabel }}
                  <template v-if="row.node.key === currentFacilityKey"> · current facility</template>
                </span>
              </div>
            </TableCell>
            <TableCell v-for="cell of row.cells" :key="cell.app.appID" class="py-1.5 text-center">
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded p-0.5 hover:ring-2 hover:ring-slate-300"
                :title="cellTitle(row.node, cell)"
                @click="editStart(row.node, cell.app)"
              >
                <span
                  class="inline-flex h-6 w-6 items-center justify-center rounded"
                  :class="
                    cell.effective.decidedBy === null
                      ? 'text-slate-300'
                      : cell.isExplicit
                        ? cell.effective.isGrantedAccess
                          ? 'bg-emerald-600 text-white'
                          : 'bg-red-600 text-white'
                        : cell.effective.isGrantedAccess
                          ? 'border border-dashed border-emerald-500 text-emerald-600'
                          : 'border border-dashed border-red-400 text-red-500'
                  "
                >
                  <i
                    :class="
                      cell.effective.decidedBy === null
                        ? 'fa-light fa-minus'
                        : cell.effective.isGrantedAccess
                          ? 'fa-solid fa-check'
                          : 'fa-solid fa-ban'
                    "
                    class="text-xs"
                  ></i>
                </span>
                <span v-if="cell.counts" class="rounded bg-amber-100 px-1 text-xs font-medium text-amber-800">
                  {{ cell.counts.granted }}/{{ cell.counts.total }}
                </span>
              </button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>

    <AppAccessEditDialog
      v-if="editing"
      v-model:isOpen="editOpen"
      :node="editing.node"
      :app="editing.app"
      @saved="overviewRefresh"
    />
  </Card>
</template>
